import React, { useState } from 'react';
import { useFeatures } from '../../context/FeatureContext';
import { Alert, AlertDescription } from '../ui/alert';
import {
  SettingsSection,
  SettingsHeader,
  ToggleRow
} from './SettingsUI';

const FeatureSettings = () => {
  const {
    features,
    loading,
    error,
    enableFeature,
    disableFeature
  } = useFeatures();

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const featureDescriptions = {
    automations: 'Automation rules and triggers',
    scenes: 'Scene management and control',
    devices: 'Device management and control',
    smartLocks: 'Smart lock integration',
    remoteAccess: 'FRP remote access configuration',
    systemSettings: 'System configuration and settings'
  };

  const handleToggleFeature = async (featureName, currentlyEnabled) => {
    try {
      setIsUpdating(true);
      setUpdateError(null);

      const result = currentlyEnabled 
        ? await disableFeature(featureName)
        : await enableFeature(featureName);

      if (!result.success) {
        setUpdateError(result.error || 'Failed to update feature');
      }
    } catch (err) {
      setUpdateError('Failed to update feature: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatFeatureName = (name) => {
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1');
  };

  if (loading) {
    return (
      <div className="py-4">
        <SettingsHeader title="Features" />
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <SettingsHeader 
        title="Features" 
        description="Control which features are available"
      />

      {(error || updateError) && (
        <div className="px-4">
          <Alert variant="destructive">
            <AlertDescription>{error || updateError}</AlertDescription>
          </Alert>
        </div>
      )}

      <SettingsSection>
        {Object.entries(features).map(([featureName, featureConfig]) => (
          <ToggleRow
            key={featureName}
            label={formatFeatureName(featureName)}
            description={featureConfig.description || featureDescriptions[featureName]}
            checked={featureConfig.enabled || false}
            onCheckedChange={() => handleToggleFeature(featureName, featureConfig.enabled)}
            disabled={isUpdating}
          />
        ))}
      </SettingsSection>

      <div className="px-4">
        <p className="text-xs text-muted-foreground">
          Disabled features will be hidden from navigation. System Settings cannot be disabled.
        </p>
      </div>
    </div>
  );
};

export default FeatureSettings;
